import React, { useEffect, useState } from 'react';

import Modal from '../common/Modal';
// Import the CSS file for styling

import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';
import { validateRequiredFields } from "../../utils/formValidation";
import { deepTrim } from "../../utils/formValidation";

const WaSystemVariablePage = () => {

  const waApi = useWaApi();
  // Only a SYSTEM_ADMIN or ADMIN may change anything here; a USER gets a read-only view. The service refuses
  // the writes too, so this just keeps the controls out of sight.
  const { canManageWhatsAppSetup } = useUserProfileAccess();
  const [systemVariables, setSystemVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false); // State for modal visibility
  const [newSystemVariable, setNewSystemVariable] = useState({
    variableName: '',
    variableDesc: '',
    variableValue: '',
    variableDefaultValue: '',
    variableRemarks: '',
    status: true, // Default value
  });
  const [editingSystemVariable, setEditingSystemVariable] = useState(null); // Track which system variable is being edited
  const [editValue, setEditValue] = useState(''); // Track edited description
  const [editDescription, setEditDescription] = useState(''); // Track edited description
  const [editDefaultValue, setEditDefaultValue] = useState(''); // Track edited default value
  const [editRemark, setEditRemark] = useState(''); // Track edited remark value
  const [actionErrors, setActionErrors] = useState({});
  const [createError, setCreateError] = useState(null);
  const [createMessage, setCreateMessage] = useState(null);

  // State for filters
  const initialFilters = {
    id: null,
    variableName: '',
    variableValue: '',
    variableDefaultValue: '',
    status: null
  };
  const [filters, setFilters] = useState(initialFilters);
  
  const resetFilters = () => {
    clearMessages();
    setFilters(initialFilters);
  };

  //for handling pagination
  const [page, setPage] = useState(0); // Starting from the first page
  const [size, setSize] = useState(3); // Number of items per page
  const [totalPages, setTotalPages] = useState(0); // To store total pages  
  const [formError, setFormError] = useState({});
  const [editFormError, setEditFormError] = useState({});

  const resetEditForm = () => {
    setEditingSystemVariable(null);
    setEditValue('');
    setEditDescription('');
    setEditDefaultValue('');
    setEditRemark('');
  };

  const clearMessages = () => {
    setCreateError(null);
    setCreateMessage(null);
    setActionErrors({});
    setFormError({});
    setEditFormError({});
    resetEditForm();
  };

  useEffect(() => {

    const fetchSystemVariables = async () => {
      try {
        const cleanedFilters = Object.fromEntries(
          Object.entries(filters).map(([key, value]) => [
            key,
            value === '' ? null : value,
          ])
        );
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_SYSTEM_VARIABLES_BY_PAGE_ENDPOINT,
          {
            params: {
              ...cleanedFilters,
              page,
              size,
            },
          }
        );
        // Extract data from the response
        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) { // Check for successful status
          setSystemVariables(object.content); // Set the templates from the response object
          setTotalPages(object.totalPages);
        } else {
          setError(`Error: ${message}`); // Handle non-success status code
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching systemVariables:', err);
        setError(err.message || 'An error occurred.');
        setLoading(false);

      }
    };

    fetchSystemVariables();
  }, [waApi, page, size]);

  useEffect(() => {
    if (modalOpen) {
      setFormError({});
      setEditFormError({});
    }
  }, [modalOpen]);

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;

    setNewSystemVariable((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value, // Use checked for checkbox type
    }));
  };

  const handleEditClick = (systemVariable) => {
    clearMessages();
    // Set the system variable being edited
    setEditingSystemVariable(systemVariable.id);
    setEditValue(systemVariable.variableValue)//set value to edit
    setEditDescription(systemVariable.variableDesc); // Set the description to edit
    setEditDefaultValue(systemVariable.variableDefaultValue); //set the default vaule to edit
    setEditRemark(systemVariable.variableRemarks);//set the remarks
  };

  const handleEditSubmit = async (systemVariableId) => {

    setCreateError(null);
    setCreateMessage(null);
    setActionErrors((prev) => ({ ...prev, [systemVariableId]: null }));
    setEditFormError({});
    const editPayload = {
        variableValue: editValue,
        variableDesc: editDescription,
        variableDefaultValue: editDefaultValue,
        variableRemarks: editRemark
    };
    const validation = validateRequiredFields({
        variableValue: {
          value: editPayload.variableValue,
          label: "System Variable Value"
        },
        variableDefaultValue: {
          value: editPayload.variableDefaultValue,
          label: "System Variable Default Value"
        },
        variableDesc: {
          value: editPayload.variableDesc,
          label: "System Variable Description"
        }
    });
    let errors = { ...validation.errors };
        if (Object.keys(errors).length > 0) {
      setEditFormError(errors);
      return;
    }
    setEditFormError({});
    try {
      // Update systemVariable with PUT request
      const trimmedEditPayload = deepTrim(editPayload);
      const response = await waApi.put(
        `${process.env.REACT_APP_PUT_WA_SYSTEM_VARIABLE_ENDPOINT}/${systemVariableId}`,

          trimmedEditPayload

      );

      // Refresh the systemVariables list after the update
      const updatedSystemVariables = systemVariables.map((systemVariable) => {
        if (systemVariable.id === systemVariableId) {
          return { ...systemVariable, variableValue: trimmedEditPayload.variableValue, variableDesc: trimmedEditPayload.variableDesc, variableDefaultValue: trimmedEditPayload.variableDefaultValue, variableRemarks: trimmedEditPayload.variableRemarks }; // Update the edited system variable
        }
        return systemVariable; // Keep the other records unchanged
      });
      setSystemVariables(updatedSystemVariables);
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [systemVariableId]: err.message || 'An error occurred while updating the Template variable.' }));
    } finally {
      resetEditForm();
    }
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    clearMessages();
    const validation = validateRequiredFields({
        variableName: {
          value: newSystemVariable.variableName,
          label: "System Variable Name"
        },
        variableValue: {
          value: newSystemVariable.variableValue,
          label: "System Variable Value"
        },
        variableDefaultValue: {
          value: newSystemVariable.variableDefaultValue,
          label: "System Variable Default Value"
        },
        variableDesc: {
          value: newSystemVariable.variableDesc,
          label: "System Variable Description"
        }
    });
    let errors = { ...validation.errors };
        if (Object.keys(errors).length > 0) {
      setFormError(errors);
      return;
    }
    try {
        const payload = deepTrim(newSystemVariable);
        const response = await waApi.post(
          `${process.env.REACT_APP_SET_WA_SYSTEM_VARIABLE_ENDPOINT}`,
          payload
      );
      if (response.data.httpStatus === 200) {
        setSystemVariables((prev) => [...prev, response.data.object]); // Update with new systemVariable
        setNewSystemVariable({
          variableName: '',
          variableDesc: '',
          variableValue: '',
          variableDefaultValue: '',
          variableRemarks: '',
          status: true, // Default value
        });
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while creating the systemVariable.');
    } finally {
      setModalOpen(false); // Close modal after submission
    }
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };
      return newFilters;
    });
  };

  // Handle the status change to convert to boolean
  const handleStatusChange = (e) => {
    const value = e.target.value;
    const statusValue = value === 'Active' ? true : value === 'Inactive' ? false : null;

    setFilters((prev) => {
      const newFilters = { ...prev, status: statusValue };
      return newFilters;
    });
  };

  const refreshCache = async () => {
    clearMessages();
    try {
      const response = await waApi.get(
        process.env.REACT_APP_REFRESH_WA_SYSTEM_VARIABLE_CACHE_ENDPOINT
      );
      if (response.data.httpStatus === 200) {
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while refreshing the cache.');
    }
  };

  const toggleActivationStatus = async (systemVariableId, currentStatus) => {
    clearMessages();
    try {
      const newStatus = !currentStatus; // Toggle the current status
      const response = await waApi.patch(
        `${process.env.REACT_APP_SET_WA_SYSTEM_VARIABLE_STATUS_ENDPOINT}/${systemVariableId}`,
        { status: newStatus }
      );
      setSystemVariables((prev) =>
        prev.map((systemVariable) => systemVariable.id === systemVariableId ? { ...systemVariable, status: newStatus } : systemVariable)
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [systemVariableId]: err.message || 'An error occurred while updating the status.' }));
    }
  };

  // New function to apply filters
  const applyFilters = async (pageParam) => {

    setLoading(true); // Show loading while fetching filtered notifications
    clearMessages();
    var hitApplyFilter = null;
    if(page === 0) {
      hitApplyFilter = true;
    } else {
      hitApplyFilter = false;
    }
    if(pageParam===0) {
      setPage(0);
    }
    if(hitApplyFilter) {
      try {
        const cleanedFilters = Object.fromEntries(
          Object.entries(filters).map(([key, value]) => [
            key,
            value === '' ? null : value,
          ])
        );
       const response = await waApi.get(
         process.env
          .REACT_APP_GET_ALL_WA_SYSTEM_VARIABLES_BY_PAGE_ENDPOINT,
         {
           params: {
             ...cleanedFilters,
             page,
             size,
           },
         }
       );
      // Extract data from the response
      const { message, object, httpStatus } = response.data;

      if (httpStatus === 200) { // Check for successful status
        setSystemVariables(object.content); // Set the templates from the response object
        setTotalPages(object.totalPages);
      } else {
        setCreateError(message); // Handle non-success status code
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching systemVariables:', err);
      setCreateError(err.message || 'An error occurred.');
      setLoading(false);

    }
  }
  };

  const handleNextPage = () => {
    clearMessages();
    if (page + 1 < totalPages) {
      setPage((prevPage) => prevPage + 1); // Increment page number
    }
  };

  const handlePreviousPage = () => {
    clearMessages();
    if (page > 0) {
      setPage((prevPage) => prevPage - 1); // Decrement page number
    }
  };

  const handleSizeChange = (e) => {
    clearMessages();
    setSize(e.target.value);
    setPage(0); // Reset to first page on changing size
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="page-container">
      <h1>App System Variables</h1>
      <div className="flex-row filter-section">
        <div className='filter-item'>
          <label htmlFor="items-per-page">Items per page: </label>
          <select id="items-per-page" onChange={handleSizeChange} className="w-auto">
            <option value={3}>default</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </div>
      </div>

      <h3>Filters</h3>
      {/* Filter Form */}
      <div className="flex-row filter-section">
        <div className="filter-item">
          <label>Id:</label>
          <input
            type="text"
            name="id"
            value={filters.id ?? ''}
            onChange={handleFilterInputChange}
            placeholder="Only numeric value"
          />
        </div>

        <div className="filter-item">
          <label>Name:</label>
          <input
            type="text"
            name="variableName"
            value={filters.variableName ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Value:</label>
          <input
            type="text"
            name="variableValue"
            value={filters.variableValue ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Default Value:</label>
          <input
            type="text"
            name="variableDefaultValue"
            value={filters.variableDefaultValue ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Status:</label>
          <select name="status" onChange={handleStatusChange} value={filters.status === null ? '' : filters.status ? 'Active' : 'Inactive'}>
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <button type="button" onClick={() => applyFilters(0)}>Apply Filters</button>
        <button type="button" onClick={resetFilters}> Reset Filters</button>

      </div>

      {/* Button to open the modal */}
      {canManageWhatsAppSetup && (
        <div className="action-bar">
          <button
            onClick={() => {
              clearMessages();
              setNewSystemVariable({
                variableName: '',
                variableDesc: '',
                variableValue: '',
                variableDefaultValue: '',
                variableRemarks: '',
                status: true, // Default value
              });
              setModalOpen(true);
            }}
          >
            + Create New
          </button>

          <button type="button" onClick={refreshCache}>
            Refresh Cache
          </button>
        </div>
      )}

      {createMessage && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createMessage}
        </div>
      )}

      {createError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createError}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => { clearMessages(); setModalOpen(false); }}>
        <div className="page-container">
          <h3>Create New System Variable</h3>
          {formError && Object.values(formError).some(val => val !== null && val !== undefined && val !== "") && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
              <strong>Please fix the following errors:</strong>
              <ul style={{ margin: "8px 0 0 20px" }}>
                {Object.entries(formError)
                  .filter(([_, val]) => val !== null && val !== undefined && val !== "")
                  .map(([key, err], index) => (
                    <li key={index}>
                      {typeof err === "object" ? JSON.stringify(err) : String(err)}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <form onSubmit={handleSubmit} className="standard-form">
            <div className="form-group">
              <label>Name:</label>
              <input
                type="text"
                name="variableName"
                value={newSystemVariable.variableName}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Value:</label>
              <input
                type="text"
                name="variableValue"
                value={newSystemVariable.variableValue}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Description:</label>
              <input
                type="text"
                name="variableDesc"
                value={newSystemVariable.variableDesc}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Default value:</label>
              <input
                type="text"
                name="variableDefaultValue"
                value={newSystemVariable.variableDefaultValue}
                onChange={handleInputChange}
               />
            </div>

            <div className="form-group">
              <label>Remarks:</label>
              <input
                type="text"
                name="variableRemarks"
                value={newSystemVariable.variableRemarks}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Activation Status:</label>
              <input
                type="checkbox"
                name="status"
                checked={newSystemVariable.status}
                onChange={handleInputChange}
              />
            </div>

            <button type="submit">Create System Variable</button>
          </form>
        </div>
      </Modal>

      <h3>Existing App System Variables</h3>
      {systemVariables.length === 0 ? (
        <p>No System Variables available</p>
      ) : (
        <div className="table-responsive">
        {editFormError && Object.values(editFormError).some(val => val !== null && val !== undefined && val !== "") && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
              <strong>Please fix the following errors:</strong>
              <ul style={{ margin: "8px 0 0 20px" }}>
                {Object.entries(editFormError)
                  .filter(([_, val]) => val !== null && val !== undefined && val !== "")
                  .map(([key, err], index) => (
                    <li key={index}>
                      {typeof err === "object" ? JSON.stringify(err) : String(err)}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <table className="notifications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Value</th>
                <th>Description</th>
                <th>Default</th>
                <th>Remark</th>
                <th>Created At</th>
                <th>Status</th>
                <th>Last Updated At</th>
                <th>Last Updated By</th>
                {canManageWhatsAppSetup && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {systemVariables.map((systemVariable) => (
                <tr key={systemVariable.id}>
                  <td>{systemVariable.id}</td>
                  <td>{systemVariable.variableName}</td>
                  <td>
                    {editingSystemVariable === systemVariable.id ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)} // Handle edit value change
                      />
                    ) : (
                      systemVariable.variableValue
                    )}
                  </td>
                  <td>
                    {editingSystemVariable === systemVariable.id ? (
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)} // Handle edit description change
                      />
                    ) : (
                      systemVariable.variableDesc
                    )}
                  </td>
                  <td>
                    {editingSystemVariable === systemVariable.id ? (
                      <input
                        type="text"
                        value={editDefaultValue}
                        onChange={(e) => setEditDefaultValue(e.target.value)} // Handle edit Default change
                      />
                    ) : (
                      systemVariable.variableDefaultValue
                    )}
                  </td>
                  <td>
                    {editingSystemVariable === systemVariable.id ? (
                      <input
                        type="text"
                        value={editRemark}
                        onChange={(e) => setEditRemark(e.target.value)} // Handle edit Remark change
                      />
                    ) : (
                      systemVariable.variableRemarks
                    )}
                  </td>
                  <td>{new Date(systemVariable.createdTimestamp).toLocaleString()}</td>
                  <td>{systemVariable.status ? 'Active' : 'Inactive'}</td>
                  <td>{new Date(systemVariable.updatedTimestamp).toLocaleString()}</td>
                  <td>{systemVariable.lastUpdatedBy}</td>
                  {canManageWhatsAppSetup && (
                    <td>
                     <div className="action-buttons">
                          {editingSystemVariable === systemVariable.id ? (
                          <>
                            <button onClick={() => handleEditSubmit(systemVariable.id)}>Save</button>
                            <button type="button" onClick={clearMessages}>Cancel</button>
                          </>
                        )  : (
                          <button onClick={() => handleEditClick(systemVariable)}>Edit</button>
                        )}
                        <button onClick={() => toggleActivationStatus(systemVariable.id, systemVariable.status)}>
                          {systemVariable.status ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                      {actionErrors[systemVariable.id] && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                          {actionErrors[systemVariable.id]}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Pagination Controls */}
      <div className="pagination-container">
        <button onClick={handlePreviousPage} disabled={page === 0}>Previous</button>
        <span>Page {page + 1} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page + 1 === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default WaSystemVariablePage;
